import React, { useState } from 'react';
import { 
  Box, 
  SpeedDial, 
  SpeedDialAction, 
  SpeedDialIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Snackbar,
  Alert
} from '@mui/material';
import { Plus, ListChecks, Lightbulb, LayoutDashboard, AudioLines, X, Receipt, Newspaper, CircleDollarSign, Palmtree, Package } from 'lucide-react';
import QuickTasks from '../../components/QuickTasks';
import QuickIdeaForm from '../../components/ideas/QuickIdeaForm';
import AddItemDialog from '../cooler/inventory/AddItemDialog';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';

// Create a client
const queryClient = new QueryClient();

interface DialogProps {
  open: boolean;
  onClose: () => void;
}

const QuickTaskDialog: React.FC<DialogProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Quick Add Task</Typography>
          <IconButton onClick={onClose} size="small">
            <X />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <QueryClientProvider client={queryClient}>
          <QuickTasks />
        </QueryClientProvider>
      </DialogContent>
    </Dialog>
  );
};

const QuickIdeaDialog: React.FC<DialogProps> = ({ open, onClose }) => {
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const handleSubmitSuccess = () => {
    setNotification({
      open: true,
      message: 'Idea saved successfully!',
      severity: 'success'
    });
    onClose();
  };

  const handleSubmitError = (error: string) => {
    setNotification({
      open: true,
      message: error || 'Failed to save idea',
      severity: 'error'
    });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Quick Add Idea</Typography>
            <IconButton onClick={onClose} size="small">
              <X />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <QuickIdeaForm
              onSubmit={handleSubmitSuccess}
              onError={handleSubmitError}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const QuickTaskboardDialog: React.FC<DialogProps> = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Quick Add Taskboard</Typography>
          <IconButton onClick={onClose} size="small">
            <X />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography>Taskboard form coming soon...</Typography>
      </DialogContent>
    </Dialog>
  );
};

const QuickAddButton: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [activeDialog, setActiveDialog] = useState<'task' | 'idea' | 'taskboard' | 'band' | 'inventory' | null>(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const actions = [
    { 
      icon: (
          <ListChecks size={30} color="#059669" strokeWidth={2.5} />
      ),
      name: 'New Task', 
      handler: () => setActiveDialog('task') 
    },
    { 
      icon: (
          <Lightbulb size={30} color="#D97706" strokeWidth={2.5} />
      ),
      name: 'New Idea', 
      handler: () => setActiveDialog('idea') 
    },
    { 
      icon: (
          <LayoutDashboard size={30} color="#2563EB" strokeWidth={2.5} />
      ),
      name: 'New Taskboard', 
      handler: () => setActiveDialog('taskboard') 
    },
    { 
      icon: (
          <AudioLines size={30} color="#7C3AED" strokeWidth={2.5} />
      ),
      name: 'New Band', 
      handler: () => navigate('/submit')
    },
    { 
      icon: (
          <Package size={30} color="#EC4899" strokeWidth={2.5} />
      ),
      name: 'New Inventory Item', 
      handler: () => setActiveDialog('inventory') 
    },
    { 
      icon: (
          <CircleDollarSign size={30} color="#DC2626" strokeWidth={2.5} />
      ),
      name: 'New Expense', 
      handler: () => navigate('/admin/expenses/new')
    },
    { 
      icon: (
          <Newspaper size={30} color="#0891B2" strokeWidth={2.5} />
      ),
      name: 'New Invoice', 
      handler: () => navigate('/admin/invoices/new')
    },
    {
     icon: <Palmtree size={30} color="#ca8a04" strokeWidth={2.5} />,
     name: 'New TIL or AL', 
     handler: () => navigate('/admin/rota?scroll=calendar')
    }
  ];

  const handleClose = () => {
    setActiveDialog(null);
  };

  const handleInventorySubmit = async (item: any) => {
    try {
      console.log('Quick adding inventory item:', item);
      
      const { selectedImageFiles, selectedProofFile, ...itemData } = item;
      
      const formData = new FormData();
      formData.append('data', JSON.stringify(itemData));
      
      // Add compressed image files
      if (selectedImageFiles?.length) {
        selectedImageFiles.forEach((file: File) => {
          formData.append('equipmentImages', file);
        });
      }
      
      // Add proof files
      if (selectedProofFile?.length) {
        selectedProofFile.forEach((file: File) => {
          formData.append('equipmentDocs', file);
        });
      }
      
      const response = await axiosInstance.post('/api/Inventory/create-atomic', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setNotification({
        open: true,
        message: `"${item.name}" added to inventory successfully!`,
        severity: 'success'
      });
      
      handleClose();
    } catch (error) {
      console.error('Failed to add inventory item:', error);
      setNotification({
        open: true,
        message: 'Failed to add inventory item',
        severity: 'error'
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <Box 
        sx={{ 
          position: 'fixed', 
          bottom: 16, 
          right: 16, 
          zIndex: 1000 
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <SpeedDial
          ariaLabel="Quick Add SpeedDial"
          icon={<SpeedDialIcon icon={<Plus />} />}
          open={open}
          direction="up"
          FabProps={{
            onMouseEnter: () => setOpen(true),
          }}
          sx={{
            '& .MuiSpeedDial-fab': {
              width: 56,
              height: 56,
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            },
            '& .MuiSpeedDial-actions': {
              paddingBottom: '8px',
            },
          }}
        >
          {actions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  {action.icon}
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 60,
                      backgroundColor: 'background.paper',
                      color: 'text.primary',
                      padding: '6px 12px',
                      borderRadius: 1,
                      boxShadow: 2,
                      whiteSpace: 'nowrap',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      border: '1px solid',
                      borderColor: 'divider',
                      minWidth: 120,
                      textAlign: 'center',
                      display: open ? 'block' : 'none'
                    }}
                  >
                    {action.name}
                  </Box>
                </Box>
              }
              tooltipTitle=""
              onClick={() => {
                setOpen(false);
                action.handler();
              }}
              FabProps={{
                onMouseEnter: () => setOpen(true),
              }}
              sx={{
                width: 40,
                height: 40,
              }}
            />
          ))}
        </SpeedDial>
      </Box>

      {activeDialog === 'task' && (
        <QuickTaskDialog open={true} onClose={handleClose} />
      )}
      {activeDialog === 'idea' && (
        <QuickIdeaDialog open={true} onClose={handleClose} />
      )}
      {activeDialog === 'taskboard' && (
        <QuickTaskboardDialog open={true} onClose={handleClose} />
      )}
      {activeDialog === 'band' && (
        <QuickTaskboardDialog open={true} onClose={handleClose} />
      )}
      {activeDialog === 'inventory' && (
        <AddItemDialog 
          open={true} 
          onClose={handleClose}
          onSave={handleInventorySubmit}
        />
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default QuickAddButton;